// src/components/BackgroundToolbar.js
import { useEffect } from "react";
import { Box, Typography, Divider } from "@mui/material";
import { Gradient } from "fabric";
import GradientColorPickerComp from "./GradientColorPickerComp";
import { useData } from "../hooks/useData";

const BackgroundToolbar = ({ canvas }) => {
  const { saveHistory, setBackgroundColor, setSnackbar } = useData();

  const setCanvasBackground = (value) => {
    if (!canvas) {
      console.error("BackgroundToolbar: Canvas not initialized");
      setSnackbar({
        open: true,
        message: "Canvas not initialized.",
        severity: "error",
      });
      return;
    }

    try {
      // Validate color value
      if (
        !value ||
        (typeof value === "string" && !value.match(/^#[0-9A-Fa-f]{6}$|^rgb/))
      ) {
        throw new Error("Invalid color value");
      }
      canvas.backgroundColor = value;
      canvas.requestRenderAll();
      setBackgroundColor(value);
      console.log("BackgroundToolbar: Set background color", {
        backgroundColor: value,
        isGradient: typeof value === "object" && value.type,
        canvasWidth: canvas.getWidth(),
        canvasHeight: canvas.getHeight(),
        viewportTransform: canvas.viewportTransform,
        zoom: canvas.getZoom(),
        objects: canvas.getObjects().map((obj) => ({
          type: obj.type,
          top: obj.top,
          left: obj.left,
        })),
      });
      saveHistory(canvas);
    } catch (error) {
      console.error("BackgroundToolbar: Failed to set background color", {
        error: error.message,
        value,
      });
      setSnackbar({
        open: true,
        message: "Error setting background color: " + error.message,
        severity: "error",
      });
    }
  };

  useEffect(() => {
    if (canvas) {
      canvas.on("selection:created", () => {
        canvas.set({ historyProcessing: true });
      });
      canvas.on("selection:cleared", () => {
        canvas.set({ historyProcessing: true });
      });
    }
  }, [canvas]);

  const handleColorChange = (color) => {
    if (!canvas) {
      console.error("BackgroundToolbar: Canvas not initialized");
      setSnackbar({
        open: true,
        message: "Canvas not initialized.",
        severity: "error",
      });
      return;
    }

    console.log("BackgroundToolbar: Processing color change", { color });

    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    try {
      if (color.includes("linear")) {
        const tokens = color.substring(23).trim();
        const cleanTokens = tokens.slice(0, -1);
        const cleanerTokens = cleanTokens
          .replace(/\s*,\s*/g, ",")
          .replace(/%,/g, "%, ");
        const rgbStops = cleanerTokens.split(", ");
        const rgbStopsObject = rgbStops.map((x) => ({
          offset: isNaN(
            Number(x.substring(x.indexOf(" ")).replace("%", "").trim() / 100)
          )
            ? 0
            : Number(x.substring(x.indexOf(" ")).replace("%", "").trim() / 100),
          color: x.substring(0, x.indexOf(" ")).trim(),
        }));
        rgbStopsObject.forEach((stop) => {
          if (stop.offset > 1) stop.offset = 1;
        });
        const gradient = new Gradient({
          type: "linear",
          gradientUnits: "pixels",
          coords: { x1: 0, y1: 0, x2: canvasWidth, y2: 0 },
          colorStops: rgbStopsObject,
        });
        setCanvasBackground(gradient);
      } else if (color.includes("radial")) {
        const tokens = color.substring(23).trim();
        const cleanTokens = tokens.slice(0, -1);
        const cleanerTokens = cleanTokens
          .replace(/\s*,\s*/g, ",")
          .replace(/%,/g, "%, ");
        const rgbStops = cleanerTokens.split(", ");
        const rgbStopsObject = rgbStops.map((x) => ({
          offset: isNaN(
            Number(x.substring(x.indexOf(" ")).replace("%", "").trim() / 100)
          )
            ? 0
            : Number(x.substring(x.indexOf(" ")).replace("%", "").trim() / 100),
          color: x.substring(0, x.indexOf(" ")).trim(),
        }));
        rgbStopsObject.forEach((stop) => {
          if (stop.offset > 1) stop.offset = 1;
        });
        const gradient = new Gradient({
          type: "radial",
          coords: {
            x1: canvasWidth / 2,
            y1: canvasHeight / 2,
            x2: canvasWidth / 2,
            y2: canvasHeight / 2,
            r1: 1,
            r2: canvasWidth / 2,
          },
          colorStops: rgbStopsObject,
        });
        setCanvasBackground(gradient);
      } else {
        setCanvasBackground(color);
      }
    } catch (error) {
      console.error("BackgroundToolbar: Failed to process color change", {
        error: error.message,
        color,
      });
      setSnackbar({
        open: true,
        message: "Error processing background color: " + error.message,
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          color: "#0d2445",
          fontFamily: "Roboto",
          fontWeight: 700,
          textAlign: "left",
        }}
      >
        Select background color
      </Typography>
      <Divider
        sx={{
          borderBottomWidth: 1,
          borderColor: "grey",
          marginTop: "12px",
          marginBottom: "18px",
        }}
        variant="fullWidth"
        orientation="horizontal"
      />
      <Box>
        <GradientColorPickerComp handleChangeColor={handleColorChange} />
      </Box>
    </Box>
  );
};

export default BackgroundToolbar;
